// Car-driven effects (RALLY_GOAL.md §11): lingering lit dust plumes on gravel, gravel spray, mud
// spray, water splashes, tarmac tyre smoke, anti-lag flames and tyre marks. Driven from the sim's
// wheel contacts after each frame; no allocation in the per-frame path except Rapier's linvel().
import * as THREE from 'three/webgpu';
import { MatteNodeMaterial } from './matte';
import type { Vehicle } from '../sim/vehicle';
import type { SurfaceId } from '../sim/types';
import { ParticleLayer } from './particles';

const DUST: Partial<Record<SurfaceId, number>> = { gravel: 1, loose: 1.35, grass: 0.25, rock: 0.5, concrete: 0.1 };
const DUST_COL: Partial<Record<SurfaceId, [number, number, number]>> = { gravel: [0.74, 0.66, 0.53], loose: [0.78, 0.7, 0.57], grass: [0.6, 0.56, 0.46], rock: [0.7, 0.68, 0.64], concrete: [0.7, 0.7, 0.68] };

let seed = 99;
const rnd = () => {
  seed = (seed * 16807) % 2147483647;
  return seed / 2147483647;
};
const sym = () => rnd() * 2 - 1;

/** Ring buffer of tyre-mark quads, one strip per wheel. */
class TyreMarks {
  readonly mesh: THREE.Mesh;
  private readonly cap: number;
  private head = 0;
  private filled = 0;
  private readonly pos: THREE.BufferAttribute;
  private readonly col: THREE.BufferAttribute;
  private readonly last: { x: number; y: number; z: number; lx: number; lz: number; on: boolean }[] = [];

  constructor(capacity = 4000) {
    this.cap = capacity;
    const g = new THREE.BufferGeometry();
    this.pos = new THREE.BufferAttribute(new Float32Array(capacity * 4 * 3), 3);
    this.col = new THREE.BufferAttribute(new Float32Array(capacity * 4 * 4), 4);
    const nor = new Float32Array(capacity * 4 * 3);
    for (let i = 0; i < capacity * 4; i++) nor[i * 3 + 1] = 1;
    const idx = new Uint32Array(capacity * 6);
    for (let i = 0; i < capacity; i++) {
      const b = i * 4;
      idx.set([b, b + 1, b + 2, b, b + 2, b + 3], i * 6);
    }
    g.setAttribute('position', this.pos);
    g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    g.setAttribute('color', this.col);
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    g.setDrawRange(0, 0);
    // Matte like the ground they're pressed into (a glossy strip read as painted tape).
    const m = new MatteNodeMaterial({ vertexColors: true, transparent: true, depthWrite: false, roughness: 0.95, metalness: 0 });
    m.polygonOffset = true;
    m.polygonOffsetFactor = -3;
    m.polygonOffsetUnits = -3;
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.receiveShadow = true;
    this.mesh.renderOrder = 1;
    this.mesh.visible = false;
    for (let i = 0; i < 4; i++) this.last.push({ x: 0, y: 0, z: 0, lx: 0, lz: 0, on: false });
  }

  reset(): void {
    for (const l of this.last) l.on = false;
  }

  /** Extend wheel `w`'s mark to (x, y, z); `alpha` 0 lifts the pen. */
  add(w: number, x: number, y: number, z: number, width: number, r: number, g: number, b: number, alpha: number): void {
    const l = this.last[w];
    if (alpha <= 0.01) {
      l.on = false;
      return;
    }
    if (!l.on) {
      l.x = x;
      l.y = y;
      l.z = z;
      l.lx = 0;
      l.lz = 0;
      l.on = true;
      return;
    }
    const dx = x - l.x;
    const dz = z - l.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.35) return;
    if (d > 3) {
      l.x = x;
      l.y = y;
      l.z = z;
      l.on = true;
      return;
    }
    // Perpendicular half-width.
    const px = (-dz / d) * width * 0.5;
    const pz = (dx / d) * width * 0.5;
    const i = this.head;
    const P = this.pos.array as Float32Array;
    const C = this.col.array as Float32Array;
    const o = i * 12;
    const prevPx = l.lx || px;
    const prevPz = l.lz || pz;
    P[o] = l.x - prevPx;
    P[o + 1] = l.y;
    P[o + 2] = l.z - prevPz;
    P[o + 3] = l.x + prevPx;
    P[o + 4] = l.y;
    P[o + 5] = l.z + prevPz;
    P[o + 6] = x + px;
    P[o + 7] = y;
    P[o + 8] = z + pz;
    P[o + 9] = x - px;
    P[o + 10] = y;
    P[o + 11] = z - pz;
    for (let k = 0; k < 4; k++) {
      const c = i * 16 + k * 4;
      C[c] = r;
      C[c + 1] = g;
      C[c + 2] = b;
      C[c + 3] = alpha;
    }
    this.pos.addUpdateRange(o, 12);
    this.col.addUpdateRange(i * 16, 16);
    this.pos.needsUpdate = true;
    this.col.needsUpdate = true;
    this.head = (this.head + 1) % this.cap;
    this.filled = Math.min(this.cap, this.filled + 1);
    this.mesh.geometry.setDrawRange(0, this.filled * 6);
    this.mesh.visible = true;
    l.x = x;
    l.y = y;
    l.z = z;
    l.lx = px;
    l.lz = pz;
  }

}

export class VehicleEffects {
  readonly group = new THREE.Group();
  readonly dust: ParticleLayer;
  readonly stones: ParticleLayer;
  readonly mud: ParticleLayer;
  readonly spray: ParticleLayer;
  readonly smoke: ParticleLayer;
  readonly flames: ParticleLayer;
  readonly marks = new TyreMarks();
  /** Particle-count scale from the quality preset. */
  amount = 1;
  wind = { x: 1.1, z: 0.5 };
  private readonly acc = new Float32Array(4 * 5);
  private lastPops = 0;
  private readonly tmpV = new THREE.Vector3();
  private readonly fwd = new THREE.Vector3();
  private readonly q = new THREE.Quaternion();
  /** The particle layers, for the per-frame update loop (no array per frame). */
  private readonly layers: ParticleLayer[];

  constructor() {
    this.dust = new ParticleLayer({ capacity: 1500, shape: 'puff', gravity: -0.12, drag: 0.9, windFollow: 1, fadeIn: 0.12, lit: true, floorLift: 0.3 });
    this.smoke = new ParticleLayer({ capacity: 300, shape: 'puff', gravity: -0.25, drag: 1.2, windFollow: 1, fadeIn: 0.1, lit: true, floorLift: 0.25 });
    this.stones = new ParticleLayer({ capacity: 700, shape: 'chip', gravity: 9.81, drag: 0.15, windFollow: 0, fadeIn: 0.01, lit: true });
    this.mud = new ParticleLayer({ capacity: 600, shape: 'chip', gravity: 9.81, drag: 0.3, windFollow: 0, fadeIn: 0.01, lit: true });
    this.spray = new ParticleLayer({ capacity: 500, shape: 'puff', gravity: 7.5, drag: 0.8, windFollow: 0.3, fadeIn: 0.05, lit: true });
    this.flames = new ParticleLayer({ capacity: 64, shape: 'flame', additive: true, gravity: 0, drag: 6, windFollow: 0, fadeIn: 0.01, lit: false });
    this.layers = [this.dust, this.smoke, this.stones, this.mud, this.spray, this.flames];
    for (const l of [this.marks.mesh, this.dust.mesh, this.smoke.mesh, this.stones.mesh, this.mud.mesh, this.spray.mesh, this.flames.mesh]) this.group.add(l);
  }

  reset(): void {
    for (const l of [this.dust, this.smoke, this.stones, this.mud, this.spray, this.flames]) l.clear();
    this.marks.reset();
  }

  /**
   * Per frame after the sim: `exhaustWorld` is the exhaust tip in world space, `exhaustDir` the
   * direction the pipe points (world, unit).
   */
  update(v: Vehicle, dt: number, exhaustWorld: THREE.Vector3, exhaustDir: THREE.Vector3, light: THREE.Color): void {
    ParticleLayer.light.value.copy(light);
    const lv = v.body.linvel();
    const r = v.body.rotation();
    this.q.set(r.x, r.y, r.z, r.w);
    this.fwd.set(0, 0, 1).applyQuaternion(this.q);
    const carSpeed = Math.hypot(lv.x, lv.y, lv.z);
    const k = this.amount;
    for (let i = 0; i < 4; i++) {
      const w = v.wheels[i];
      if (!w.contact) {
        this.marks.add(i, 0, 0, 0, 0, 0, 0, 0, 0);
        continue;
      }
      const cp = w.contactPoint;
      const sp = Math.hypot(w.vx, w.vy);
      const slip = Math.min(1.5, Math.abs(w.slipRatio) * 1.4 + Math.abs(w.slipAngle) * 1.2);
      const surf = w.surface;
      const rear = !w.front;
      // --- Dust plume (the signature rally image): mostly from the rear wheels.
      const dusty = DUST[surf] ?? 0;
      if (dusty > 0 && sp > 3) {
        const rate = sp * 0.7 * dusty * (rear ? 1 : 0.45) * (1 + 1.5 * slip) * k;
        const a = (this.acc[i * 5] += rate * dt);
        const c = DUST_COL[surf] ?? DUST_COL.gravel!;
        for (let n = Math.floor(a); n > 0; n--) {
          this.dust.spawn(cp.x + sym() * 0.3, cp.y, cp.z + sym() * 0.3, lv.x * 0.18 + sym() * 0.8, 0.9 + rnd() * 1.2, lv.z * 0.18 + sym() * 0.8, 3.5 + rnd() * 3.5, 1.6 + rnd() * 0.9, 7 + rnd() * 4.5, c[0], c[1], c[2], 0.42 + 0.14 * Math.min(1, slip));
        }
        this.acc[i * 5] = a - Math.floor(a);
      }
      // --- Gravel spray: stones thrown back and up by the tyre.
      if ((surf === 'gravel' || surf === 'loose') && sp > 2) {
        const rate = sp * (0.25 + 3 * slip) * (surf === 'loose' ? 1.5 : 1) * k;
        const a = (this.acc[i * 5 + 1] += rate * dt);
        for (let n = Math.floor(a); n > 0; n--) {
          const back = -(4 + rnd() * 4 + slip * 4);
          this.stones.spawn(cp.x, cp.y + 0.08, cp.z, lv.x * 0.6 + this.fwd.x * back + sym() * 1.8, 1.5 + rnd() * 3.5, lv.z * 0.6 + this.fwd.z * back + sym() * 1.8, 0.5 + rnd() * 0.6, 0.03 + rnd() * 0.035, 0.04, 0.5, 0.45, 0.38, 1);
        }
        this.acc[i * 5 + 1] = a - Math.floor(a);
      }
      // --- Mud spray.
      if ((surf === 'mud' || surf === 'wet') && sp > 2) {
        const rate = sp * (0.6 + 4 * slip) * k;
        const a = (this.acc[i * 5 + 2] += rate * dt);
        for (let n = Math.floor(a); n > 0; n--) {
          const back = -(3 + rnd() * 4 + slip * 5);
          this.mud.spawn(cp.x, cp.y + 0.1, cp.z, lv.x * 0.5 + this.fwd.x * back + sym() * 1.5, 2 + rnd() * 4, lv.z * 0.5 + this.fwd.z * back + sym() * 1.5, 0.6 + rnd() * 0.6, 0.06 + rnd() * 0.1, 0.1, 0.2, 0.15, 0.1, 1);
        }
        this.acc[i * 5 + 2] = a - Math.floor(a);
      }
      // --- Water splash.
      if (surf === 'water' && sp > 1.5) {
        const rate = sp * 3.2 * k;
        const a = (this.acc[i * 5 + 3] += rate * dt);
        const side = w.left ? 1 : -1;
        for (let n = Math.floor(a); n > 0; n--) {
          // Fan out sideways (+X is the car's left).
          this.tmpV.set(side * (2 + rnd() * 4), 0, 0).applyQuaternion(this.q);
          this.spray.spawn(cp.x, cp.y + 0.15, cp.z, lv.x * 0.7 + this.tmpV.x + sym(), 2.5 + rnd() * 4 + sp * 0.08, lv.z * 0.7 + this.tmpV.z + sym(), 0.6 + rnd() * 0.6, 0.35, 1.4 + rnd() * 1.2, 0.85, 0.88, 0.9, 0.65);
        }
        this.acc[i * 5 + 3] = a - Math.floor(a);
      }
      // --- Tarmac tyre smoke when sliding or spinning hard.
      if ((surf === 'tarmac' || surf === 'concrete') && slip > 0.35 && sp > 2) {
        const a = (this.acc[i * 5 + 4] += (slip - 0.3) * 30 * k * dt);
        for (let n = Math.floor(a); n > 0; n--) this.smoke.spawn(cp.x, cp.y, cp.z, lv.x * 0.2 + sym() * 0.5, 0.3 + rnd() * 0.4, lv.z * 0.2 + sym() * 0.5, 1.5 + rnd() * 1.5, 0.6, 3.5, 0.85, 0.85, 0.85, 0.18);
        this.acc[i * 5 + 4] = a - Math.floor(a);
      }
      // --- Tyre marks: rubber on tarmac only when sliding; compacted grooves on loose surfaces.
      let alpha = 0;
      let mr = 0.05;
      let mg = 0.045;
      let mb = 0.04;
      if (surf === 'tarmac' || surf === 'concrete') alpha = Math.max(0, slip - 0.18) * 0.7;
      else if (surf === 'gravel' || surf === 'loose') {
        alpha = 0.14 + Math.min(0.26, slip * 0.35);
        mr = 0.16;
        mg = 0.12;
        mb = 0.085;
      } else if (surf === 'mud' || surf === 'wet') {
        alpha = 0.45;
        mr = 0.08;
        mg = 0.06;
        mb = 0.04;
      } else if (surf === 'grass') {
        alpha = 0.25 + Math.min(0.3, slip * 0.4);
        mr = 0.12;
        mg = 0.14;
        mb = 0.06;
      }
      this.marks.add(i, cp.x, cp.y + 0.02, cp.z, v.spec.tyre.width, mr, mg, mb, Math.min(0.75, alpha));
    }
    // --- Anti-lag pops: a short burst of flame from the exhaust per pop.
    const pops = v.drivetrain.popCount;
    if (pops !== this.lastPops) {
      const bursts = Math.min(3, pops - this.lastPops);
      this.lastPops = pops;
      for (let b = 0; b < bursts; b++) {
        for (let n = 0; n < 4; n++) {
          const sp = 5 + rnd() * 7;
          this.flames.spawn(exhaustWorld.x, exhaustWorld.y, exhaustWorld.z, lv.x + exhaustDir.x * sp + sym() * 0.6, lv.y + exhaustDir.y * sp + sym() * 0.4, lv.z + exhaustDir.z * sp + sym() * 0.6, 0.05 + rnd() * 0.07, 0.18, 0.45 + rnd() * 0.3, 1, 0.75 + rnd() * 0.2, 0.5, 1);
        }
        this.smoke.spawn(exhaustWorld.x, exhaustWorld.y, exhaustWorld.z, lv.x * 0.5 + exhaustDir.x * 2, 0.4, lv.z * 0.5 + exhaustDir.z * 2, 0.8 + rnd() * 0.6, 0.3, 1.6, 0.45, 0.45, 0.45, 0.25);
      }
    }
    void carSpeed;
    for (const l of this.layers) l.update(dt, this.wind.x, this.wind.z);
  }
}
